/** Warm dusky rose-mauve used for the dark-theme fog + background + canvas
 *  backdrop. Golden-hour sunset, not night — mid-tone so the island stays
 *  clearly legible while harmonizing with the app's warm-dark chrome. */
export const NIGHT_BG = "#6F4D5E";
/** Bright noon sky used for day fog + background + canvas backdrop. */
export const DAY_BG = "#5B8FA8";

/** Key sun sharing one soft-shadow rig across both grades — only position,
 *  intensity, and color change between high noon (day) and the low golden-hour
 *  angle (night). Soft pastel shadows need Canvas `shadows="soft"`. */
function KeySun({
  position,
  intensity,
  color,
}: {
  position: [number, number, number];
  intensity: number;
  color: string;
}) {
  return (
    <directionalLight
      position={position}
      intensity={intensity}
      color={color}
      castShadow
      shadow-mapSize={[2048, 2048]}
      shadow-bias={-0.001}
      shadow-normalBias={0.02}
      shadow-radius={8}
      shadow-camera-left={-12}
      shadow-camera-right={12}
      shadow-camera-top={12}
      shadow-camera-bottom={-12}
    />
  );
}

export function EnvironmentSetup({ isNight = false }: { isNight?: boolean }) {
  if (isNight) {
    return (
      <>
        {/* Sunset grade: soft rose sky ambient, a low golden-orange key sun
            (lower elevation than noon so shadows stretch long), and a cool
            periwinkle fill so shadows stay believable. Bright enough to read
            every object — golden hour, not gloom. The CRT point light is the
            only glow. */}
        <ambientLight intensity={0.58} color="#F2C4B0" />
        <KeySun position={[8, 7, 5]} intensity={0.75} color="#FFA85C" />
        <directionalLight position={[-5, 8, -3]} intensity={0.25} color="#7D89C4" />
        <fog attach="fog" args={[NIGHT_BG, 24, 46]} />
        <color attach="background" args={[NIGHT_BG]} />
      </>
    );
  }

  return (
    <>
      {/* Monument Valley grade, matched to the landing hero
          (HeroWorld/HeroEnvironment): ambient dominates so colors stay
          bright, the sun is gentle, and shadows read as soft pastel tints
          (shadow-radius needs Canvas shadows="soft"). */}
      <ambientLight intensity={1.05} color="#E9F0F4" />
      <KeySun position={[8, 15, 5]} intensity={0.85} color="#FFF6E8" />
      <directionalLight position={[-5, 8, -3]} intensity={0.4} color="#BCE0EE" />
      <fog attach="fog" args={[DAY_BG, 26, 46]} />
      <color attach="background" args={[DAY_BG]} />
    </>
  );
}
